import { FetchOptions } from 'bernie-core';
import { logMetaExperiment } from './experiment_helper_46906';
import { ExperimentStore } from 'bernie-plugin-mobx';
import { MetaQueryAdapter, SearchQueryAdapter } from 'stores/adapters';
import { isDomesticJourney, setIsDomestic } from 'utility/flights-controller-utils';
import {
  FlightsAdvancedFilterType,
  FlightsJourneyCriteriaInput,
  FlightsShoppingPwaFlightSearchResultsFlightsSearch,
  FlightsTravelerDetailsInput,
  FlightsTravelerType
} from '__generated__/api';
import { SearchParametersType } from 'src/types/common';
import { toJS } from 'mobx';
import { getPackageTypeSymbol } from 'utility';
import { SearchCriteriaStore } from 'stores';

const ONE_WAY = 'OneWay';

export const FlightsMetaFIStoFSR = {
  meetsMetaExperimentConditions: async (options: FetchOptions): Promise<boolean> => {
    const departureCities = options?.query?.dcty;
    const arrivalCities = options?.query?.acty;
    const departureDates = options?.query?.ddte;
    const journey = MetaQueryAdapter.getFlightJourneyCriteria(departureCities, arrivalCities, departureDates);
    const originDestinations = await isDomesticJourney(options, journey[0]?.origin, journey[0]?.destination);
    const isDomestic = setIsDomestic(originDestinations);

    return options.query.tytr == ONE_WAY && isDomestic;
  },
  createSearchParameters: (
    {
      flightsSearchContext,
      travelerDetails,
      journeyCriteria,
      searchPreferences,
      searchFilterValuesList,
      sortOption,
      shoppingContext
    }: FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables,
    isMetaExperiment?: boolean,
    metaJourneysContinuationId?: string
  ): SearchParametersType => {
    const flightRouteType = flightsSearchContext.tripType || '';
    let journeysContinuationId = flightsSearchContext.journeysContinuationId || null;

    if (isMetaExperiment && metaJourneysContinuationId) {
      journeysContinuationId = metaJourneysContinuationId;
    }

    const originalBookingId = flightsSearchContext.originalBookingId || null;
    const previousOriginalBookingId = flightsSearchContext.previousOriginalBookingId || null;

    const legSearchInfoList = (journeyCriteria as FlightsJourneyCriteriaInput[]).map(leg => ({
      departureLocation: leg.origin,
      arrivalLocation: leg.destination,
      departureDate: toJS(leg.departureDate)
    }));
    const normalizedTravelerDetails = travelerDetails as FlightsTravelerDetailsInput[];
    let travelerCategoryInfo;
    if (normalizedTravelerDetails.length > 0) {
      const child = normalizedTravelerDetails.find(e => e.travelerType === FlightsTravelerType.CHILD);
      const adult = normalizedTravelerDetails.find(e => e.travelerType === FlightsTravelerType.ADULT);
      const infantsInSeat = normalizedTravelerDetails.find(e => e.travelerType === FlightsTravelerType.INFANT_IN_SEAT);
      const infantsInLap = normalizedTravelerDetails.find(e => e.travelerType === FlightsTravelerType.INFANT_IN_LAP);
      const adultCount = adult ? adult.count : 0;
      const totalPassengers = normalizedTravelerDetails.reduce((a, { count }) => count + a, 0);
      const infantSeatingOnLap = infantsInLap !== undefined;
      let childAges = child ? toJS(child.ages) : [];
      let childCount = child ? child.count : 0;

      if (infantsInSeat) {
        childCount += infantsInSeat.count;
        childAges = childAges.concat(toJS(infantsInSeat.ages));
      }

      if (infantsInLap) {
        childCount += infantsInLap.count;
        childAges = childAges.concat(toJS(infantsInLap.ages));
      }

      travelerCategoryInfo = {
        adultCount,
        childAges,
        childCount,
        infantSeatingOnLap,
        totalPassengers
      };
    }

    const airlinePreferenceCode = searchPreferences.airline || '';
    const flightAirCabinClass = searchPreferences.cabinClass || '';
    const refundableFlightsOnly =
      searchPreferences.advancedFilters &&
      searchPreferences.advancedFilters.find(e => e === FlightsAdvancedFilterType.REFUNDABLE)
        ? 'Y'
        : 'N';
    const nonstopOnly =
      searchPreferences.advancedFilters &&
      searchPreferences.advancedFilters.find(e => e === FlightsAdvancedFilterType.NONSTOP)
        ? 0
        : 1;

    const misId = shoppingContext?.multiItem?.id || null;
    const packageType = getPackageTypeSymbol(shoppingContext?.multiItem?.packageType) || null;

    return {
      flightRouteType,
      legSearchInfoList,
      travelerCategoryInfo,
      airlinePreferenceCode,
      flightAirCabinClass,
      refundableFlightsOnly,
      nonstopOnly,
      searchFilterValuesList,
      sortOption,
      journeysContinuationId,
      originalBookingId,
      previousOriginalBookingId,
      misId,
      packageType
    };
  },
  isOneWay(query): boolean {
    return query?.trip === 'oneway';
  },
  setMetaCriteria(options: FetchOptions, isDomestic: boolean): void {
    const searchCriteriaStore = options.stores.get<SearchCriteriaStore>('searchCriteriaStore');
    const experiment = options.stores.get<ExperimentStore>('experiment');
    const isMetaExperimentTnLEnabled = logMetaExperiment.isMetaExperimentEnabled(experiment);
    const isOneWay = FlightsMetaFIStoFSR.isOneWay(options.query);
    searchCriteriaStore.setIsMetaExperiment(isMetaExperimentTnLEnabled && isDomestic && isOneWay);
  },
  setMetaExperimentCriteriaFromUrl(
    fetchOptions: FetchOptions,
    searchCriteriaStore: SearchCriteriaStore,
    isMetaExperiment?: boolean
  ) {
    searchCriteriaStore.isMetaExperiment = isMetaExperiment;
    const query = fetchOptions.query;
    const queryMap = new Map<string, string>();
    Object.keys(query).map(params => queryMap.set(params, query[params]));

    const options = (query && query.options && query.options.split(',')) || [];
    queryMap.delete('options');
    queryMap.delete('price');
    queryMap.delete('ccyc');
    const numberOfAdults = query.cadt;
    queryMap.delete('cadt');
    const numberOfSeniors = query.csen;
    queryMap.delete('csen');
    const ages = query.ages;
    queryMap.delete('ages');
    const infantInLap = query.filp;
    queryMap.delete('filp');
    const cabinClass = query.ccls;
    queryMap.delete('ccls');
    const departureDates = query.ddte;
    queryMap.delete('ddte');
    const departureCities = query.dcty;
    queryMap.delete('dcty');
    const arrivalCities = query.acty;
    queryMap.delete('acty');
    queryMap.delete('rfft');
    const carrier = query.carrier;
    queryMap.delete('carrier');
    const tripType = query.tytr;
    queryMap.delete('tytr');
    queryMap.delete('prevQuery');
    const originalBookingId = query && query.originalBookingId;
    queryMap.delete('originalBookingId');
    const journeyIdentifier = query.piid;
    queryMap.delete('piid');

    searchCriteriaStore.searchCriteria.flightsSearchContext.originalBookingId = originalBookingId || null;

    searchCriteriaStore.searchCriteria.flightsSearchContext.tripType = SearchQueryAdapter.mapTripType(tripType);

    searchCriteriaStore.flexSearchCriteria.flightsSearchContext.tripType = SearchQueryAdapter.mapTripType(tripType);

    searchCriteriaStore.searchFormCriteria.flightsSearchContext.tripType = SearchQueryAdapter.mapTripType(tripType);

    searchCriteriaStore.searchCriteria.searchPreferences.advancedFilters = SearchQueryAdapter.mapAdvancedFilters(
      options
    );

    searchCriteriaStore.flexSearchCriteria.searchPreferences.advancedFilters = SearchQueryAdapter.mapAdvancedFilters(
      options
    );

    searchCriteriaStore.searchFormCriteria.searchPreferences.advancedFilters = SearchQueryAdapter.mapAdvancedFilters(
      options
    );

    searchCriteriaStore.initVirtualAgentContextIfAvailable();

    searchCriteriaStore.searchCriteria.searchPreferences.airline = SearchQueryAdapter.mapAirline(carrier);

    searchCriteriaStore.flexSearchCriteria.searchPreferences.airline = SearchQueryAdapter.mapAirline(
      options.find(option => option.includes('carrier'))
    );

    searchCriteriaStore.searchFormCriteria.searchPreferences.airline = SearchQueryAdapter.mapAirline(carrier);

    searchCriteriaStore.searchCriteria.searchPreferences.cabinClass = SearchQueryAdapter.mapCabinClass(cabinClass);

    searchCriteriaStore.flexSearchCriteria.searchPreferences.cabinClass = SearchQueryAdapter.mapCabinClass(cabinClass);

    searchCriteriaStore.searchFormCriteria.searchPreferences.cabinClass = SearchQueryAdapter.mapCabinClass(cabinClass);

    searchCriteriaStore.searchCriteria.travelerDetails = MetaQueryAdapter.getTravelerDetails(
      numberOfAdults,
      numberOfSeniors,
      ages,
      infantInLap
    );

    searchCriteriaStore.flexSearchCriteria.travelerDetails = MetaQueryAdapter.getTravelerDetails(
      numberOfAdults,
      numberOfSeniors,
      ages,
      infantInLap
    );

    searchCriteriaStore.searchFormCriteria.travelerDetails = MetaQueryAdapter.getTravelerDetails(
      numberOfAdults,
      numberOfSeniors,
      ages,
      infantInLap
    );

    searchCriteriaStore.searchCriteria.journeyCriteria = MetaQueryAdapter.getFlightJourneyCriteria(
      departureCities,
      arrivalCities,
      departureDates
    );

    searchCriteriaStore.flexSearchCriteria.journeyCriteria = MetaQueryAdapter.getFlightJourneyCriteria(
      departureCities,
      arrivalCities,
      departureDates
    );

    searchCriteriaStore.searchFormCriteria.journeyCriteria = MetaQueryAdapter.getFlightJourneyCriteria(
      departureCities,
      arrivalCities,
      departureDates
    );

    searchCriteriaStore.searchCriteria.flightsSearchComponentCriteria.isMetaTraffic = isMetaExperiment;
    searchCriteriaStore.searchCriteria.flightsSearchComponentCriteria.journeyIdentifier = journeyIdentifier;

    if (queryMap.has('paandi')) {
      for (const [key, value] of queryMap) {
        searchCriteriaStore.searchCriteria.flightsSearchComponentCriteria.queryParams.push({
          key: key,
          value: value?.toString()
        });
      }
    } else {
      searchCriteriaStore.searchCriteria.flightsSearchComponentCriteria.queryParams = [];
    }
  }
};
